/** English — the base dictionary and fallback for any missing key. */
const en = {
  nav: {
    dashboard: 'Dashboard',
    products: 'Products',
    website: 'Website',
    advertorials: 'Advertorials',
    ads: 'Ads',
    splitTesting: 'Split testing',
    findUpsells: 'Find upsells',
    findProducts: 'Find products',
    aiChat: 'AI Chat',
    referAndEarn: 'Refer & earn',
    settings: 'Settings',
    notifications: 'Notifications',
    aiSuggestions: 'AI Suggestions',
    noSuggestions: 'No suggestions yet — check back soon!',
    caughtUp: 'You’re all caught up',
    noNotificationsBody: 'We’ll tell you here when a build finishes or the AI has a suggestion for your store.',
    // Group labels in the navigation dropdown (rendered uppercase).
    groups: {
      create: 'Create',
      grow: 'Grow',
      assistant: 'Assistant',
      account: 'Account',
    },
  },
  common: {
    generate: 'Generate',
    publish: 'Publish',
    download: 'Download',
    downloadAll: 'Download all',
    save: 'Save',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    continue: 'Continue',
    done: 'Done',
    view: 'View',
    edit: 'Edit',
    delete: 'Delete',
    close: 'Close',
    loading: 'Loading...',
    loadingStore: 'Loading your store…',
    product: 'Product',
    selectProduct: 'Select a product',
    pleaseChooseProduct: 'Please choose a product',
    markComplete: 'Mark as complete',
    copyHtml: 'Copy HTML',
    settingsSaved: 'Settings saved',
    alreadyGenerating: 'This is already generating — check the progress indicator at the bottom right.',
    generationStarted: "Generation started — you can leave this page, we'll notify you when it's done.",
    open: 'Open',
    tryAgain: 'Try again',
    viewLive: 'View live page',
    preview: 'Preview',
  },
  dashboard: {
    title: 'Dashboard',
    subtitle: 'Build products, pages, ads and websites — then let the AI keep improving them.',
    primaryAction: 'Create a product',
    capabilitiesTitle: 'Everything Autonoma can do',
    capabilitiesSubtitle: 'Pick a tool to get started. Features marked “Automatic” run on their own once your ads have data.',
    automatic: 'Automatic',
    automaticHint: 'Runs on its own once your ads have data — nothing to set up.',
    gettingStarted: {
      title: 'Getting started',
      progress: '{done} of {total} done — the next step is highlighted.',
      hide: 'Hide',
      doneCta: 'Done',
      product: { title: 'Create a product', desc: 'Upload a photo — we build the product, images and page.', cta: 'Create a product' },
      ads: { title: 'Create ads', desc: 'Image ads, video ads and ad copy for your product.', cta: 'Create ads' },
      meta: { title: 'Connect Meta & launch', desc: 'Connect your ad account, then publish a campaign.', cta: 'Connect Meta' },
      website: { title: 'Build a website', desc: 'A full branded storefront around your products.', cta: 'Build a website' },
    },
    suggestions: 'AI Suggestions',
    dismiss: 'Dismiss',
    adPerformance: 'Ad performance — last 30 days',
    badgeNew: 'New',
    badgeComingSoon: 'Coming soon',
    metrics: {
      totalSpend: 'Total spend',
      roas: 'ROAS',
      purchases: 'Purchases',
      revenue: 'Revenue',
      clicks: 'Clicks',
      ctr: 'CTR',
    },
    capabilities: {
      buildProducts: {
        title: 'Build products from scratch',
        desc: 'Upload a photo or paste a URL — AI creates the full product, images, pricing and a high-converting product page.',
      },
      fullWebsite: {
        title: 'Create a full website',
        desc: 'Homepage, about, contact — a complete branded store built around your product.',
      },
      imageAds: {
        title: 'Image ads',
        desc: '20 professional DTC ad creatives per product, ready for Meta.',
      },
      videoAds: {
        title: 'Video ads',
        desc: 'AI-generated video ads proven to convert on Meta and TikTok.',
      },
      advertorials: {
        title: 'Advertorials',
        desc: 'Editorial-style articles that sell — story, research, listicle and more.',
      },
      releaseAds: {
        title: 'Create & release ads',
        desc: 'AI writes the copy, builds the campaign, and publishes to your Meta account.',
      },
      splitTesting: {
        title: 'Split testing',
        desc: 'Test pricing, deals, images, copy and product pages to find what wins.',
      },
      scaling: {
        title: 'Scaling suggestions',
        desc: 'AI tracks winning ads and tells you exactly what to scale and when.',
      },
      winningAds: {
        title: 'Winning ad detection',
        desc: 'AI finds your best performers and creates more ads based on what works.',
      },
      aiAssistant: {
        title: 'AI Assistant',
        desc: 'Ask it anything — change a price, fix a product page, set up a test, scale an ad.',
      },
      themeEditorAi: {
        title: 'Autonoma AI in the theme editor',
        desc: 'Click any section in Shopify’s theme editor and tell the AI what to change — on any theme.',
      },
      mobileApp: {
        title: 'Manage from your phone',
        desc: 'Run your store and launch ads on the go from the mobile app.',
      },
    },
    mobileAppModal: {
      title: 'Get the Autonoma app on your phone',
      subtitle: 'Copy the link and open it on your device — or open the store page directly.',
      ios: 'iOS — App Store',
      android: 'Android — Google Play',
      open: 'Open',
      copyLink: 'Copy link',
      copied: 'Copied',
      close: 'Close',
    },
  },
  ads: {
    imageN: 'Image {n}',
    brief: {
      label: 'Brief', open: 'See the image brief', title: 'Image brief', close: 'Close',
      layout: 'Layout', purpose: 'Purpose', attempts: '{n} attempts', textRendered: 'Text rendered by us', compositeUsed: 'Real product composited',
      pass: 'pass', fail: 'fail', gateIdentity: 'Identity', gateText: 'Text', gateAesthetics: 'Aesthetics', gateArrows: 'Arrows',
      overlays: 'Text on the image (exact)', camera: 'Camera', lighting: 'Lighting', background: 'Background', negativeSpace: 'Negative space',
      finalPrompt: 'Final prompt', copy: 'Copy', copied: 'Copied',
    },
    title: 'Ads',
    subtitle: 'Create, review, and publish Meta ads — step by step',
    tabCreate: 'Create new',
    tabInProgress: 'In progress',
    tabCompleted: 'Completed',
    tabStrategy: 'Strategy',
    stepSetup: 'Setup',
    stepCreatives: 'Creatives',
    stepCopy: 'Copy',
    stepPublish: 'Publish',
    generateAds: 'Generate ads',
    clickToSelect: 'Click images to select them.',
    campaignSetup: 'Campaign setup',
    campaignName: 'Campaign name',
    adSetName: 'Ad set name',
    dailyBudget: 'Daily budget',
    countries: 'Countries',
    publishAds: 'Publish ads',
    connectAdAccount: 'Connect ad account',
    setup: {
      account: {
        title: 'Connect your Meta ad account to publish',
        body: 'Your ads are ready. Publishing needs a connected Meta ad account — it takes about a minute in Settings, then come back here.',
        cta: 'Connect ad account',
      },
      page: {
        title: 'Select a Facebook Page to publish',
        body: 'Meta needs a Facebook Page to create the ad creatives. Pick one in Settings, then come back here.',
        cta: 'Select a Facebook Page',
      },
      pixel: {
        title: 'Set up your Meta Pixel to publish',
        body: 'Purchase-optimized ads need your Pixel with the Purchase event. Set it up in Settings, then come back here.',
        cta: 'Set up your Pixel',
      },
    },
    connectToPublish: 'Please connect your ad account to publish.',
    adCopy: 'Ad copy',
    generateCopy: 'Generate ad copy',
    useExistingCopy: 'Use existing ad copy',
    noInProgress: 'No ads in progress. Start one from the Create new tab — your progress saves automatically after each step.',
    noCompleted: 'No completed ad runs yet.',
    createNewAds: 'Create new ads',
    imageAds: 'Image ads',
    videoAds: 'Video ads',
    writeManually: 'Write manually',
    manualCopyTitle: 'Write your own ad copy',
    manualCopyHelp: 'The first primary text and first headline are required — the rest are optional but recommended so Meta can test more variations.',
    primaryText: 'Primary text',
    headline: 'Headline',
    required: 'required',
    recommended: 'recommended',
    manualValidation: 'Add at least the first primary text and the first headline to continue.',
    emptyInProgressTitle: 'Nothing in progress',
    emptyCompletedTitle: 'No completed ad runs yet',
    emptyCompletedBody: 'Finished ad runs show here, read-only — start one from Create new.',
    progressTitle: 'Creating your ads',
    progressSteps: {
      study: 'Studying your product photos',
      design: 'Designing your ad creatives',
      render: 'Rendering your images',
      renderVideo: 'Rendering your images and videos',
      review: 'Preparing them for review',
    },
    cancelGeneration: 'Cancel this generation',
    cancelConfirm: 'Cancel this generation?',
    cancelTitle: 'Cancel generation',
    liveTitle: 'Your campaign is live 🚀',
    liveBody: '{n} ad(s) are running and spending your daily budget.',
    pausedTitle: 'Your campaign is created — it’s turned off 🎉',
    pausedBody: 'Nothing is spending yet. Turn it on when you’re ready, or review it in Meta first.',
    viewInAdsManager: 'View in Ads Manager',
    createMore: 'Create more ads',
    turnOnTitle: 'Turn the campaign on',
    turnOnDesc: 'Starts spending your daily budget right away.',
    turnOn: 'Turn on',
    reviewInMetaTitle: 'Review it in Meta first',
    reviewInMetaDesc: 'Check the ads, audience and budget in Ads Manager before spending.',
    barTitle: 'Publish {n} ad(s) to Meta',
    barActive: 'They’ll start running as soon as they’re published.',
    barPaused: 'They’re created turned off — nothing spends until you turn them on.',
    next: {
      performance: { title: 'Watch the results', desc: 'Spend, ROAS and purchases show on your dashboard once Meta reports back.', cta: 'Go to dashboard' },
      split: { title: 'Split test the landing page', desc: 'Test prices, offers and page versions to lift conversion.', cta: 'Start a split test' },
    },
  },
  advertorials: {
    title: 'Advertorials',
    subtitle: 'Generate long-form editorial pages that read like news articles and sell products',
    generate: 'Generate advertorial',
    publish: 'Publish advertorial',
    published: 'Advertorial published!',
    createAdsQuestion: 'Create ads around this angle?',
    yesCreateAds: 'Yes, create ads',
    noThanks: 'No thanks',
    tabCreate: 'Create new',
    tabPrevious: 'Previously created',
    allProducts: 'All products',
    filterByProduct: 'Filter by product',
    noPrevious: 'No advertorials yet — create your first from the Create new tab.',
    editWithAi: 'Open & edit with AI',
    viewLive: 'View live page',
    createAdsFor: 'Create ads for advertorial',
    updated: 'Advertorial updated!',
    created: 'Created',
    setupEyebrow: 'Setup',
    setupTitle: 'Choose a product and a style',
    startError: 'Couldn’t start the advertorial',
    progressTitle: 'Writing your advertorial',
    progressSteps: {
      research: 'Researching the product and audience',
      write: 'Writing the article',
      images: 'Creating images',
      layout: 'Laying out the page',
    },
    reviewEyebrow: 'Review',
    reviewTitle: 'Your advertorial',
    reviewHint: 'Read it through, click anything to change it, then publish when you’re happy.',
    draftLabel: 'Draft — not live yet',
    publishError: 'Couldn’t publish the advertorial',
    liveTitle: 'Your advertorial is live 🎉',
    editInThemeEditor: 'Edit in theme editor',
    createAnother: 'Create another advertorial',
    emptyTitle: 'No advertorials yet',
    next: {
      ads: { title: 'Create ads around this angle', desc: 'Ads that send people to your advertorial — same steps: creatives → copy → publish.' },
      edit: { title: 'See all your advertorials', desc: 'Open, edit with AI or create ads for any earlier advertorial.', cta: 'View all' },
    },
  },
  products: {
    title: 'Products',
    subtitle: 'Everything Autonoma has built or that lives in your store',
    createNew: 'Create new product',
    createImages: 'Create images',
    createPage: 'Create product page',
    loading: 'Loading your products…',
    loadError: 'Couldn’t load your products',
    emptyTitle: 'No products yet',
    emptyBody: 'Create your first product — upload a photo and we build the product, images and page.',
    statusActive: 'Active',
    statusDraft: 'Draft',
  },
  generations: {
    title: 'Previous creations',
    tabAll: 'All',
    tabTrash: 'Trash',
    trashNote: 'Deleted items stay here for {n} days — restore them or delete them permanently.',
    trashEmpty: 'Trash is empty.',
    empty: 'Nothing created yet.',
    viewEdit: 'View / edit',
    moveToTrash: 'Move to trash',
    restore: 'Restore',
    deleteForever: 'Delete permanently',
    deletedIn: 'permanently deleted {when}',
    status: { draft: 'Draft', published: 'Published', replaced: 'Replaced', trashed: 'In trash' },
  },
  productDetail: {
    loading: 'Loading product…',
    loadError: 'Couldn’t load this product',
    backToProducts: 'Back to products',
    editInShopify: 'Edit in Shopify',
    draftNote: 'Draft — publish the product in Shopify to get a live page link.',
    whatToCreate: 'What do you want to create?',
    pagesTitle: 'Product pages for this product',
    pagesEmpty: 'No product page has been created for this product yet.',
    pagesEmptyCta: 'Create the product page',
    othersTitle: 'Advertorials and ad packs for this product',
    othersEmpty: 'No advertorials or ad packs yet.',
    chatPrompt: 'Prefer to just describe the change?',
    chatCta: 'Chat with AI',
    actions: {
      ads: { title: 'Create ads for this product', desc: 'Image and video ad creatives, ready to publish to Meta.' },
      images: { title: 'Create product images', desc: 'Fresh AI product photography from your existing shots.' },
      page: { title: 'Create product page', desc: 'A high-converting product page in your chosen style.' },
      advertorial: { title: 'Create advertorial', desc: 'An editorial-style article that sells — story, research, listicle.' },
      splitTest: { title: 'Create split test', desc: 'Test prices, deals or product pages against each other.' },
      upsell: { title: 'Find upsell products', desc: 'Discover complementary products that raise your average order value.' },
    },
  },
  flow: {
    product: 'Product',
    build: 'Build',
    review: 'Review',
    publish: 'Publish',
    setup: 'Setup',
    customise: 'Customise',
    preview: 'Preview',
    results: 'Results',
  },
  newProduct: {
    addPhotos: 'Add product photos',
    variantName: 'Variant name',
    variantNameN: 'Variant {n} name',
    title: 'New product',
    subtitle: 'Upload your product photos (URL optional) — Autonoma builds the product and page',
    startError: 'Couldn’t start the build',
    barTitle: 'Ready when you are',
    barReady: 'We’ll build the product, images and page — about 5 minutes.',
    barNeedPhoto: 'Upload at least one product photo to start.',
    starting: 'Starting…',
    generateOne: 'Generate product + page',
    generateMany: 'Generate {n} products + pages',
    progressTitle: 'Building your product and page',
    progressSteps: {
      read: 'Reading your photos and notes',
      write: 'Writing your product and page',
      images: 'Creating product images',
      shopify: 'Setting it up in Shopify',
    },
    cancelBuild: 'Cancel this build',
    cancelConfirm: 'Cancel this build?',
    cancelTitle: 'Cancel build',
    cancelIt: 'Cancel it',
    previewError: 'Couldn’t load the page preview',
    loadingPreview: 'Loading your page preview…',
    sections: {
      photos: 'Photos',
      source: 'Source · optional',
      details: 'Details · optional',
      style: 'Page style',
    },
  },
  createImages: {
    settingsSummary: '{product} · {n} images in this batch',
    newBatch: 'New batch',
    progressTitle: 'Creating {n} images…',
    progressSteps: {
      study: 'Studying your product…',
      design: 'Planning each shot…',
      render: 'Rendering images…',
      finish: 'Finishing touches…',
    },
    cancelConfirm: 'Cancel creating these images?',
    cancelTitle: 'Cancel images',
    publishedTitle: '{n} images published to your product 🎉',
    publishedBody: 'The first one is now the main product image.',
    nextAds: 'Create ads with these images',
    barTitle: 'Product images',
    barSelected: '{n} of {total} selected',
    downloadSelected: 'Download {n} selected',
    downloadAll: 'Download all',
    publishN: 'Publish {n} to product page',
    title: 'Create product images',
    subtitle: 'Fresh AI product photography from your existing shots',
  },
  createPage: {
    progressTitle: 'Building your product page…',
    progressPreview: 'Preparing the preview…',
    alreadyGenerating: 'A page for this product is already being built — follow its progress in the indicator at the bottom right.',
    title: 'Create product page',
    subtitle: 'A high-converting product page for an existing product',
  },
  upsell: {
    title: 'Upsell discovery',
    subtitle: 'Find complementary products stores like yours bundle alongside this one — the fastest way to raise average order value',
    flowPick: 'Pick an upsell',
    previewTitle: 'Preview your upsell',
    publishedTitle: 'Upsell published',
    previewSubtitle: '“{upsell}” as an optional add-on for “{main}”',
    liveTitle: 'Your upsell is live 🎉',
    liveBody: 'It appears as an optional add-on next to Add to Cart on “{main}”. Customers who tick it get both items in the cart together.',
    liveBodyNoBlock: 'The upsell product and its page are live. The add-on block needs one manual step — see below.',
    blockManualTitle: 'Add the add-on block to your product page',
    viewMainPage: 'View the product page',
    viewUpsellPage: 'View the upsell page',
    nothingLiveTitle: 'Nothing is live yet',
    nothingLiveBody: 'Review both pieces below and refine them until you’re happy, then publish. Publishing activates the upsell product, makes its page live, and adds the optional add-on block next to Add to Cart on “{main}”.',
    barLive: 'Live on your store',
    barDraft: 'Draft — publish both pieces in one click',
    publishError: 'Couldn’t publish the upsell',
    publishAll: 'Publish upsell',
    republish: 'Republish',
    discoverProgressTitle: 'Finding upsells',
    discoverSteps: { scan: 'Scanning your catalogue', research: 'Researching what pairs well', rank: 'Ranking the best matches' },
    buildProgressTitle: 'Building “{name}”',
    yourUpsell: 'your upsell',
    buildSteps: { product: 'Creating the upsell product', page: 'Writing its product page', block: 'Designing the add-on block' },
    noneTitle: 'No recommendations came back',
    noneBody: 'That happens occasionally — try again in a moment.',
    next: {
      ads: { title: 'Create ads for the main product', desc: 'Send traffic to the page that now carries the upsell.', cta: 'Create ads' },
      another: { title: 'Find another upsell', desc: 'Add more add-ons to other products.', cta: 'Find upsells' },
    },
  },
  findProducts: {
    title: 'Find products',
    subtitle: 'Winning products proven by real ad spend — fresh picks on every search',
    progressTitle: 'Hunting for winning products',
    progressSteps: { scan: 'Scanning what’s selling right now', filter: 'Filtering for your niche', rank: 'Ranking the strongest picks' },
    cancelConfirm: 'Cancel this search?',
    cancelTitle: 'Cancel search',
    noneTitle: 'No winning products for this category',
    noneBody: 'Try another category or run the search again — picks refresh every time.',
  },
  splitTesting: {
    metrics: {
      variant: 'Each version being tested',
      split: 'Share of visitors who see this version',
      sessions: 'Visits to this version',
      purchases: 'Orders from this version',
      revenue: 'Sales from this version',
      cvr: 'Conversion rate — orders ÷ visits',
      aov: 'Average order value — revenue ÷ orders',
      roas: 'Return on ad spend — revenue ÷ ad spend',
      rpv: 'Revenue per visitor — revenue ÷ visits',
      legendProb: 'P(best) = chance this version is the best',
      legend: 'CVR = conversion rate · AOV = average order value · ROAS = return on ad spend · ★ = the goal of this test',
    },
    tabs: { create: 'Create new', active: 'Active', paused: 'Paused', completed: 'Completed' },
    flow: { type: 'Test type', setup: 'Set up', launch: 'Preview & launch' },
    pickProduct: 'Pick a product',
    whatToTest: 'What do you want to test?',
    previewVariants: 'Preview your variants',
    progressTitle: 'Building your variants…',
    progressSteps: {
      read: 'Reading your current page…',
      write: 'Writing each variant…',
      images: 'Creating images…',
      preview: 'Preparing the previews…',
    },
    cancelBuild: 'Cancel this build',
    cancelConfirm: 'Cancel building these variants?',
    cancelTitle: 'Cancel build',
    buildFailedTitle: 'We couldn’t build the variants',
    buildFailedHint: 'Adjust the variant descriptions above and try again.',
    runningTitle: 'Your test is running 🎉',
    runningBody: 'Traffic is now splitting between your variants. Results update live as visitors arrive.',
    viewResults: 'View results',
    viewActive: 'View active tests',
    createTest: 'Create a test',
    emptyActiveTitle: 'No active tests',
    emptyActiveBody: 'Continuous testing is how stores grow — start with a price or offer test.',
    emptyPausedTitle: 'No paused tests',
    emptyPausedBody: 'Pausing a test stops the traffic split and data collection — resume any time to pick up where it left off.',
    parity: {
      blockedTitle: 'This product can’t run a variant-product test',
      warnTitle: 'Heads-up: apps keyed on this product',
      warnFooter: 'Price, image, offer and bundle tests send the B group to a variant product. Stock, shipping profile and discounts are kept in step automatically for the whole test; the items above are keyed on the product id and may not follow. Product-page tests on your own URLs are unaffected.',
    },
    storefront: {
      pauseAll: 'Pause all tests ({n})',
      pauseAllHint: 'Emergency stop: pauses every running test and switches the storefront script off. Shoppers see your normal pages within 60 seconds.',
      deliveryTitle: 'Your tests can\'t reach shoppers yet',
      deliveryBody: 'This store has neither the legacy Autonoma script tag nor the Autonoma AI app embed switched on, so no shopper is ever assigned to a test (Shopify stopped allowing new script tags on 1 Oct 2026). Turn on the app embed in your theme editor and click Save — it takes under a minute.',
      deliveryEnable: 'Turn on the app embed',
      pauseAllConfirm: 'Pause every running test and switch the storefront split script off for this store? Shoppers will see your normal pages within 60 seconds. You can resume tests afterwards.',
      pausedAllNotice: '{n} test(s) paused and the storefront script switched off. Shoppers see your normal pages within 60 seconds.',
      offTitle: 'Split tests are OFF on your storefront',
      offBody: 'While it is off, no visitor is redirected, nothing is hidden and no cart is touched — existing assignments are ignored.',
      offPauseAll: 'You paused all tests on {since}.',
      offManual: 'The storefront script was switched off on {since}.',
      offParity: 'Autonoma switched the storefront script off on {since} because a variant product was out of step with its original (see the paused test for details).',
      offGlobal: 'Autonoma operations have switched split testing off for all stores while an issue is investigated.',
      turnOn: 'Turn storefront split tests back on',
      errorTitle: 'That didn’t work',
    },
    emptyCompletedTitle: 'No completed tests yet',
    emptyCompletedBody: 'Results land here once a test reaches its data threshold or you end it.',
    title: 'Split testing',
    subtitle: 'Test prices, pages, and offers — find what converts best',
  },
  website: {
    title: 'Website builder',
    subtitle: 'Build a multi-product storefront — homepage, navigation, logo, business name, policies, and checkout branding — around your selected products',
    startError: 'Couldn’t start the website build',
    previousTitle: 'Previous websites',
    previousEmpty: 'No website yet — build your first one below.',
    productsError: 'Couldn’t load your products',
    searchProducts: 'Search products',
    selectAll: 'Select all',
    clear: 'Clear',
    selectedCount: '{n} of {total} selected',
    noMatches: 'No products match that search.',
    showAll: 'Show all {n} products',
    showFewer: 'Show fewer',
    barTitle: 'Ready to build',
    barNoProducts: 'Create a product first.',
    barPickProducts: 'Select at least one product above.',
    barTitlePending: 'Almost ready',
    barNeedName: 'Add a business name, or tick “Let AI create my business name”.',
    barReady: 'Building around {n} product(s) — about 5 minutes.',
    buildCta: 'Build my website',
    progressTitle: 'Building your website',
    progressSteps: {
      brand: 'Shaping your brand name, colours and logo',
      homepage: 'Writing your homepage',
      images: 'Creating website images',
      pages: 'Setting up pages, menus and collections',
    },
    cancelBuild: 'Cancel this build',
    cancelConfirm: 'Cancel this website build?',
    cancelTitle: 'Cancel website build',
    liveTitle: 'Your website is live 🎉',
    liveBody: 'Homepage, About and Contact pages, policies, menus and collections are now live on your storefront.',
    viewStorefront: 'View live storefront',
    openAbout: 'Open About page',
    refineHomepage: 'Refine the homepage',
    buildAnother: 'Build another website',
    previewReadyTitle: 'Your website preview is ready',
    previewReadyBody: 'Nothing is live yet. Review the preview below, type any changes you want, then publish to push the whole site live — pages, policies, menus and collections included.',
    logo: {
      eyebrow: 'Your logo',
      title: 'Brand logo',
      dims: '{w}×{h} transparent PNG',
      sourceGenerated: 'Generated for {name}',
      sourceUploaded: 'Your uploaded logo',
      sourceTheme: 'Your current theme logo',
      sourceBrand: 'Your Shopify Brand logo',
      download: 'Download PNG (280×80)',
      generateAnother: 'Generate another',
      uploadOwn: 'Upload my own',
      undo: 'Undo — restore previous logo',
      generating: 'Designing another logo…',
      uploading: 'Uploading your logo…',
      setInTheme: 'Set in theme “{theme}” — {where}',
      whereGlobal: 'theme settings (Logo image picker)',
      whereHeader: 'header section (Logo image picker)',
      whereBoth: 'theme settings + header section',
      keptCurrent: 'Your current logo was kept — nothing in the theme was replaced.',
      notApplied: 'Not set in the theme yet',
      noLogoSetting: 'This theme has no logo image setting — download the PNG and upload it in the theme editor.',
      willApply: 'Publish the website to set it in your theme header (140px wide).',
      restored: 'Previous logo restored in the theme.',
      failed: 'Logo action failed',
      choiceTitle: 'Logo',
      choiceHelp: 'You already have a logo. Keep it, or let AI design a new one (280×80 transparent PNG).',
      useCurrent: 'Use my current logo',
      generateNew: 'Generate a new logo',
      currentFrom: 'Found in: {where}',
      currentTheme: 'your theme',
      currentBrand: 'Shopify Brand settings',
    },
    openPreview: 'Open preview & publish',
    barDraft: 'Preview — not live yet',
    pages: {
      home: 'Home',
      about: 'About',
      contact: 'Contact',
      aboutDesc: 'Your About page is written from the brand story and created on your store when you publish the website.',
      contactDesc: 'A Contact page with a working form is created on your store when you publish the website.',
    },
    next: {
      ads: { title: 'Create ads for your products', desc: 'Image ads, video ads and ad copy, ready to launch on Meta.', cta: 'Create ads' },
      split: { title: 'Split test your pages', desc: 'Test prices, offers and page versions to find what sells.', cta: 'Start a split test' },
    },
  },
  chat: {
    title: 'AI Chat',
    subtitle: 'Make changes to your store, products, and ads by just asking',
    placeholder: 'Ask Autonoma anything about growing your store...',
    send: 'Send',
    publishFailed: 'Couldn’t publish the ad copy',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your store, ad account, defaults, and plan',
    language: 'App language',
    languageHelp: 'Auto-detected from your Shopify admin — change it here if you prefer. Changes the app language AND the language of all AI-generated content (ads, pages, advertorials, video voiceovers).',
    languageSaved: 'Language updated',
    dataSharingTitle: 'Anonymised insights',
    dataSharingLabel: 'Share anonymised, aggregated usage insights to help improve Autonoma.',
    dataSharingHelp: "On by default (you can turn it off any time in Settings). When on, anonymised, aggregated patterns about which ads, pages and offers convert improve the AI's recommendations for every store. No personal data is ever shared — never your customer data, product names, sales numbers or store identity.",
    autoImproveTitle: 'Auto-improve',
    autoImproveLabel: 'Suggest a split test when a page has enough traffic and a weak add-to-cart rate.',
    autoImproveHelp: 'Off unless you turn it on. Autonoma measures how shoppers move through the sections of the pages it built (which sections are seen, for how long, and where add-to-cart happens — never who the shopper is) and, with this on, suggests one hero or offer split test per page as a notification. You review every variant before any traffic splits; nothing starts on its own.',
    autoImproveStats: '{views} page views across {pages} Autonoma pages in the last {days} days · suggestions start at {min} views per page.',
    autoImproveNoData: 'No page views measured yet — pages built with the current section library start reporting as soon as shoppers visit them.',
    onThisPage: 'On this page',
    saveFailed: 'Couldn’t save',
    metaConnectError: 'Couldn’t connect your Meta account',
    sections: {
      plan: 'Plan & usage',
      meta: 'Meta ads account',
      defaults: 'Ad defaults',
      themeEditor: 'Theme editor',
      mobile: 'Mobile app',
      referrals: 'Refer & earn',
      privacy: 'Privacy',
    },
  },
  ui: {
    nav: {
      menuLabel: 'Navigation menu',
      lockedTitle: 'Upgrade to unlock {name}',
    },
    progress: {
      done: 'Done!',
      anyMoment: 'Any moment now…',
      longer: 'Taking a little longer than usual…',
      minutesLeft: 'About {n} min left',
      oneMinuteLeft: 'About 1 min left',
      secondsLeft: 'About {n}s left',
      leaveNote: "We'll notify you when it's done — you can leave this page.",
      cancel: 'Cancel',
    },
    errors: {
      retry: 'Try again',
      reload: 'Reload the page',
      showDetails: 'Technical details',
      hideDetails: 'Hide details',
      generic: { title: 'Something didn’t work', body: 'Please try again. If it keeps happening, message us from the support button.' },
      network: { title: 'Can’t reach Autonoma', body: 'Check your internet connection and try again.' },
      session: { title: 'Your session expired', body: 'Reload the page to sign back in — your work is saved.' },
      quota: { title: 'You’ve reached your plan limit', body: 'Upgrade your plan or wait for your next billing period to continue.' },
      notFound: { title: 'We couldn’t find that', body: 'It may have been removed. Go back and try again.' },
      busy: { title: 'Already running', body: 'This is already in progress — check the progress indicator at the bottom right.' },
      tooLarge: { title: 'That file is too large', body: 'Try a smaller file.' },
      rateLimit: { title: 'Too many requests', body: 'Give it a minute and try again.' },
      timeout: { title: 'This took too long', body: 'The request timed out. Try again — it usually works the second time.' },
      server: { title: 'Something went wrong on our side', body: 'Please try again in a moment.' },
    },
    preview: {
      label: 'Preview',
      device: 'Preview device',
      desktop: 'Desktop',
      mobile: 'Mobile',
      edit: 'Edit',
      clickToEdit: 'Preview · click to edit',
      editPage: 'Edit this page',
    },
    success: {
      whatNext: 'What would you like to do next?',
    },
    session: {
      title: "We couldn't connect to your store",
      body: "Your Shopify session isn't working right now. Reconnecting usually fixes this.",
    },
    themeCode: {
      getting: 'Getting your code…',
      error: 'We couldn’t get your code',
    },
    editor: {
      opening: 'Opening your page…',
      archivedTitle: 'This page has been archived',
      openError: 'We couldn’t open this page',
      publishError: 'Publish didn’t go through',
      upToDate: '✓ Live · up to date',
      upToDateHint: 'Everything on this page is live. Make a change and the Publish button comes back.',
      back: '← Back',
    },
    jobs: {
      ads: 'Creating ads',
      advertorial: 'Building advertorial',
      landing_page: 'Building product page',
      website: 'Building website',
      images: 'Creating images',
      product_build: 'Building product',
      split_test_build: 'Building test variants',
      split_test_setup: 'Setting up split test',
      upsell_discovery: 'Finding upsells',
      upsell_build: 'Building upsell',
      product_finder: 'Finding winning products',
      edit_own_ad: 'Editing your ads',
      ad_copy: 'Writing ad copy',
      cart_upgrade: 'Upgrading your cart',
      publish_ads: 'Publishing ads',
      shipping_setup: 'Setting up shipping',
      markets_update: 'Updating markets',
      working: 'Working',
      updating: 'Updating your {what}',
    },
  },
  plans: {
    title: 'Choose your plan',
    subtitle: '{plans} include a {n}-day free trial. Cancel anytime.',
    subtitleNoTrial: 'Cancel anytime.',
    andPlans: 'and',
    perMonth: '/month',
    trialChip: '{n}-day free trial',
    billedToday: 'Billed today',
    mostPopular: 'Most popular',
    startTrial: 'Start free trial',
    choose: 'Choose {name}',
    opening: 'Opening Shopify…',
    loading: 'Loading plans',
    loadError: 'Could not load plans — please refresh the page.',
    declined: 'You need to accept a plan to continue — pick one below to get started.',
    unverified: 'We couldn’t confirm your subscription with Shopify yet. If you approved the charge, click “Check again” — otherwise pick a plan below.',
    checkAgain: 'Check again',
    checking: 'Checking…',
    contactSupport: 'Contact support',
    reactivateTitle: 'Your subscription is inactive',
    reactivateSubtitle: 'Reactivate to restore your pages, products and ads — nothing is deleted.',
    reactivateReason: {
      cancelled: 'Your Autonoma subscription was cancelled.',
      frozen: 'Your subscription is frozen — Shopify could not collect the last payment.',
      declined: 'The subscription charge was declined.',
      expired: 'Your subscription has expired.',
      pending: 'Your subscription is still waiting for approval in Shopify.',
      test_charge_on_live_store: 'This test subscription is not valid on a live store.',
      inactive: 'Your Autonoma subscription is no longer active.',
    },
    reactivateCta: 'Reactivate {name}',
    unavailableTitle: 'We couldn’t confirm your subscription',
    unavailableBody: 'Shopify didn’t answer in time, so Autonoma can’t open yet. Nothing is lost — try again in a moment.',
    tryAgain: 'Try again',
    checkingAccess: 'Checking your subscription…',
    genericError: 'Something went wrong — please try again.',
    keyNumbersTitle: 'Per month',
    notIncluded: 'Not included',
    inherits: 'Everything in {name}, plus:',
    customLine: 'Need higher limits or white-label? Custom plans for agencies and high-volume brands —',
    customCta: 'Book a call →',
    compare: 'Compare all features',
    close: 'Close',
    trustRow: 'Billed securely through Shopify · Cancel anytime · Change plan any time',
    consentTitle: 'Share anonymised insights to help improve Autonoma',
    consentBody: 'Share anonymised, aggregated usage insights to help improve Autonoma. No personal data is ever shared. You can change this any time in Settings.',
    consentDefault: 'On by default (you can turn it off any time in Settings).',
    consentInfo: 'About sharing anonymised insights',
    billingNote: 'Billing is handled securely by Shopify and appears on your Shopify invoice. By subscribing you agree to our',
    and: 'and',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    // Client fallback for backends without plan.copy.tagline / highlights
    // (the server copy in plan-copy.service.js is the source of truth).
    tiers: {
      starter: {
        tagline: 'Launch your first winning products',
        highlights: ['AI product pages', 'Image ads with sale badges', 'Advertorials that pre-sell', 'Brand website + logo'],
      },
      growth: {
        tagline: 'Publish ads and grow order value',
        highlights: ['Publish ads to Meta', 'Portrait feed ads (4:5)', 'Post-purchase upsells', 'iPhone & Android app'],
      },
      scale: {
        tagline: 'Video ads, split tests and AI chat',
        highlights: ['AI video ads', 'Split tests with clear winners', 'AI assistant that does the work', 'Priority support'],
      },
      enterprise: {
        tagline: 'For agencies and high-volume brands',
        highlights: ['Highest limits on everything', 'Test new angles every day', 'Dedicated onboarding', 'Priority support'],
      },
    },
    keyNumbers: {
      productBuilds: 'Product pages',
      imageAds: 'Image ads',
      videoAds: 'Video ads',
      advertorials: 'Advertorials',
      splitTests: 'Split tests',
      findProductsSearches: 'Product searches',
    },
    currentPlan: 'Current plan',
  },
  // Plan-limit overlay (a quota-consuming action can't fit this month's allowance)
  planLimit: {
    titleReached: 'You’ve reached your plan limit',
    titleNotEnough: 'Not enough left this month',
    titleNear: 'Running low this month',
    reached: 'You’ve used all {n} {feature} on {plan} this month. Upgrade to keep creating — your limit resets on {date}.',
    reachedBudget: 'You’ve used all of your {n} {feature} on {plan} this month. Upgrade to keep creating — your limit resets on {date}.',
    notEnough: 'You have {x} {feature} left this month. This needs {y}. Upgrade your plan to do this now.',
    notEnoughBudget: 'You have {x} of {feature} left this month. This needs {y}. Upgrade your plan to do this now.',
    notEnoughUnknown: 'You have {x} {feature} left this month — not enough for this. Upgrade your plan to do this now.',
    left: 'You have {x} {feature} left this month.',
    allowance: '{n} {feature} / month',
    allowanceBudget: '{n} {feature} / month',
    allowanceUnlimited: 'Unlimited {feature}',
    allowanceNone: 'Not on this plan',
    topTierTitle: 'You’re on our top plan',
    topTierBody: 'Need more {feature} this month? We set up Custom plans with higher limits for agencies and high-volume brands.',
    topTierCta: 'Contact us for a Custom plan',
    referLine: 'Or refer a store — you both get bonus credits.',
    referCta: 'Refer & Earn',
    nearLimit: '{n} {feature} left this month',
    nearLimitBudget: '{n} of {feature} left this month',
    viewPlans: 'View plans',
    upgraded: 'Plan upgraded — you can generate now.',
    loadError: 'Could not load plans — please try again.',
    subscribeError: 'Could not start the upgrade — please try again.',
    close: 'Close',
  },
  feedback: {
    thanksTitle: 'Thanks for the feedback',
    gotIt: 'Got it — ticket #{ticket}',
    gotItBody: 'A real person on the Autonoma team reads every one of these. If we need more detail we’ll reply to your store’s e-mail{email}.',
    backToDashboard: 'Back to dashboard',
    sendAnother: 'Send another',
  },
  featureRequest: {
    button: 'Request a feature',
    title: 'Request a feature',
    intro: 'Tell us what you’d like Autonoma to do — new features, upgrades, anything. We read every request.',
    label: 'Your request',
    placeholder: 'e.g. Let me schedule ads to start at a set time…',
    send: 'Send request',
    tooShort: 'Please tell us a little more (at least 10 characters).',
    tooLong: 'Please keep your request under 2,000 characters.',
    successTitle: 'Thanks — your request was sent.',
    successBody: 'We’ll be in touch if we need more detail.',
    reference: 'Reference #{ticket}',
    errorGeneric: 'We couldn’t send your request — please try again.',
    retry: 'Retry',
  },
  referAndEarn: {
    shareYourLink: 'Share your link',
  },
  // "Review & publish" workspace (product pages, websites, advertorials)
  review: {
    top: {
      back: 'Back',
      breadcrumb: 'Where you are',
      live: 'Live on your store',
      draft: 'Draft — not live yet',
      page: 'Page',
      part: 'Part of the page',
      productCard: 'Product card',
      landingPage: 'Landing page',
      undo: 'Undo',
      redo: 'Redo',
      history: 'Version history',
      themeEditor: 'Open in theme editor',
      viewOnStore: 'View on store',
      panel: 'Show or hide the editing panel',
      zoom: 'Preview zoom',
      zoomFit: 'Fit',
      expand: 'Expand preview',
      collapse: 'Exit full screen',
    },
    canvas: {
      toolbar: 'Actions for {label}',
      editWithAi: 'Edit with AI',
      replaceImage: 'Replace image',
      moveUp: 'Move up',
      moveDown: 'Move down',
      duplicate: 'Duplicate',
      hide: 'Hide',
      show: 'Show',
      delete: 'Delete',
      deleteIt: 'Delete it',
      thisElement: 'this element',
      deleteConfirm: 'Delete “{label}”? You can undo this afterwards.',
      locked: 'Buy buttons can’t be edited here',
      updating: 'Updating {label}…',
      updatingPage: 'Updating the whole page…',
      saving: 'Saving…',
      failed: 'The change didn’t apply',
      failedOn: 'The change to {label} didn’t apply',
      retry: 'Retry',
      plainFallback: 'showing the plain preview instead.',
    },
    composer: {
      editing: 'Editing: {label}',
      wholePage: 'Whole page',
      clearScope: 'Clear the selection',
      placeholder: 'Describe a change to the whole page…',
      placeholderScoped: 'Describe a change to the {label}…',
      wholePageBusy: 'Updating the whole page — you can keep looking around.',
      cancelConfirm: 'Cancel this update?',
      cancelTitle: 'Cancel update',
      cancelIt: 'Cancel it',
    },
    prompts: {
      pagePremium: 'Make the tone more premium',
      pageShorter: 'Shorten everything by 20%',
      pageFaq: 'Add a FAQ section',
      pageStory: 'Make the story more personal',
      pageCta: 'Make the call to action stronger',
      headlinePunchier: 'Make the headline punchier',
      headlineShorter: 'Shorten it',
      headlineBenefit: 'Lead with the main benefit',
      textTighter: 'Tighten the copy',
      textConcrete: 'Make it more concrete',
      textFriendly: 'Make it friendlier',
      buttonAction: 'Make it action-oriented',
      buttonUrgency: 'Add gentle urgency',
      imageLifestyle: 'Use a lifestyle image',
      imageBrighter: 'Brighter, cleaner background',
      sectionTrust: 'Add trust badges',
      sectionTighter: 'Tighten the copy',
      sectionTestimonial: 'Add a testimonial',
    },
    panel: {
      label: 'Editing panel',
      collapse: 'Collapse the panel',
      open: 'Panel',
      tabs: { edit: 'Edit', sections: 'Sections', images: 'Images', brand: 'Brand', details: 'Details' },
    },
    edit: {
      emptyTitle: 'Nothing selected',
      emptyBody: 'Click any part of the page to edit it.',
      inSection: 'in {section}',
      noFields: 'This part has no text or image fields — use “Edit with AI” to change it.',
      buttonLabel: '{label} · label',
      buttonLink: '{label} · link',
      saved: 'Saved',
      hintEnter: 'Enter to save',
      hintBlur: 'Saves when you click away',
      replace: 'Replace',
      generate: 'Generate',
      upload: 'Upload',
      uploading: 'Uploading…',
      uploadFailed: 'The image could not be uploaded.',
      pickImage: 'Choose an image',
      noLibrary: 'No generated images for this product yet — upload one instead.',
      useImage: 'Use image',
      generatePrompt: 'Generate a new image for this spot — same product, cleaner composition. Attach a reference if you want a specific look.',
    },
    sections: {
      hide: 'Hide {label}',
      show: 'Show {label}',
      none: 'No sections found on this page.',
      add: 'Add section',
      dragHint: 'Drag the handle to reorder. Hidden sections stay in the page but don’t show on your store.',
      libraryTitle: 'Add a section',
      libraryHint: 'AI writes the section for this page and places it after the part you are looking at.',
      placeAfter: 'Place it right after the “{section}” section.',
      library: {
        benefits: { name: 'Benefits', desc: 'Three to four benefits with icons.', instruction: 'Add a benefits section with 3–4 concrete benefits, each with a short heading, one sentence and a simple icon.' },
        testimonials: { name: 'Testimonials', desc: 'Customer quotes with names and ratings.', instruction: 'Add a testimonials section with three believable customer quotes, first names, locations and 5-star ratings.' },
        faq: { name: 'FAQ', desc: 'Five common pre-purchase questions.', instruction: 'Add a FAQ section with five real pre-purchase questions and reassuring, concrete answers.' },
        gallery: { name: 'Image gallery', desc: 'A grid of the existing images.', instruction: 'Add an image gallery section using only images already on the page, in a clean grid.' },
        comparison: { name: 'Comparison table', desc: 'Us vs. the usual alternatives.', instruction: 'Add a comparison table section: this product versus typical alternatives, 4–5 rows, with ticks and crosses.' },
        howItWorks: { name: 'How it works', desc: 'Three numbered steps.', instruction: 'Add a “How it works” section with three numbered steps, each with a short heading and one sentence.' },
        guarantee: { name: 'Guarantee', desc: 'Money-back promise and shipping notes.', instruction: 'Add a guarantee section: a money-back promise, shipping and returns in plain words, with a badge.' },
        cta: { name: 'Call to action', desc: 'A closing banner with a button.', instruction: 'Add a closing call-to-action banner section with a strong headline, one line of reassurance and a button linking to the existing buy button or product.' },
      },
    },
    brand: {
      noLogo: 'No logo was generated for this website yet.',
      colours: 'Brand colours',
      fonts: 'Fonts',
      change: 'Change',
      noColours: 'Brand colours come from your theme.',
      headingFont: 'Headings',
      bodyFont: 'Body',
      themeDefault: 'Theme default',
    },
    details: {
      product: 'Product',
      title: 'Title',
      price: 'Price',
      compareAt: 'Compare-at',
      none: 'None',
      status: 'Status',
      productHint: 'Title, price and compare-at price are read from Shopify. Edit them there — the page updates on publish.',
      editInShopify: 'Edit product in Shopify',
      advertorial: 'Advertorial',
      headline: 'Headline',
      linkedProduct: 'Linked product',
      ctaOk: 'The call-to-action links to {product}.',
      ctaOther: 'The call-to-action links to a different product.',
      ctaMissing: 'No link to the product was found — add one so readers can buy.',
      openProduct: 'Open product',
    },
    pages: {
      publishesWithSite: 'This page is created when you publish the website.',
    },
    coach: {
      step: '{n} of {total}',
      clickTitle: 'Click any part to edit',
      clickBody: 'Hover to see what you can change, click to select it — then use the floating toolbar or the panel on the right.',
      composerTitle: 'Or describe a change here',
      composerBody: 'Type what you want in plain words. With something selected the change stays inside it; otherwise it applies to the whole page.',
      publishTitle: 'Publish when you’re happy',
      publishBody: 'Nothing is live until you press Publish. Every change is saved and can be undone.',
      skip: 'Skip',
      gotIt: 'Got it',
    },
    bar: {
      publish: 'Publish to my store',
      update: 'Update live page',
      publishing: 'Publishing…',
      saveDraft: 'Save draft',
      checking: 'Checking…',
      review: 'Review',
      oneIssue: '1 issue blocks publishing',
      nIssues: '{n} issues block publishing',
      oneSuggestion: '1 suggestion',
      nSuggestions: '{n} suggestions',
      blockedTooltip: 'Publishing is blocked',
      blockedTooltipAction: 'Click to review and fix.',
      blockedBy: 'blocked by {n} issue(s)',
      whyBlocked: 'Why can’t I publish? Review the issues',
      savedJustNow: 'Saved just now',
      savedAgo: 'Saved {n} min ago',
      allSaved: 'All changes saved',
    },
  },
};

export default en;
